// VRP Error Boundary Provider - Hierarchical Error Handling
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { VRPErrorDetector } from '../utils/errorDetection';
import { VRPErrorRecovery } from '../utils/errorRecovery';
import { globalErrorReporting } from '../utils/errorReporting';
import type { VRPError, ErrorBoundaryState } from '../types/errors';

interface VRPErrorBoundaryProviderProps {
  children: ReactNode;
  level: 'application' | 'feature' | 'component';
  feature?: string;
  component?: string;
  fallback?: (error: Error, retry: () => void) => ReactNode;
  onError?: (error: VRPError) => void;
  enableRecovery?: boolean;
}

interface VRPErrorBoundaryProviderState extends ErrorBoundaryState {}

export class VRPErrorBoundaryProvider extends Component<
  VRPErrorBoundaryProviderProps,
  VRPErrorBoundaryProviderState
> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: VRPErrorBoundaryProviderProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<VRPErrorBoundaryProviderState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const vrpError = VRPErrorDetector.createVRPError(error, {
      feature: this.props.feature,
      component: this.props.component
    });

    this.setState({
      errorInfo,
      errorId: vrpError.id
    });

    // Report error
    globalErrorReporting.reportError(vrpError);

    // Call custom error handler
    this.props.onError?.(vrpError);

    // Attempt automatic recovery if enabled
    if (this.props.enableRecovery !== false) {
      this.attemptRecovery(vrpError);
    }

    console.error(
      `[VRPErrorBoundary] Error caught at ${this.props.level} level:`,
      error,
      errorInfo
    );
  }

  private async attemptRecovery(vrpError: VRPError) {
    if (!VRPErrorRecovery.shouldAttemptRecovery(vrpError)) {
      return;
    }

    console.log(`[VRPErrorBoundary] Attempting recovery for error: ${vrpError.id}`);

    try {
      const recovered = await VRPErrorRecovery.attemptRecovery(vrpError);
      
      if (recovered) {
        const recoveredError = VRPErrorRecovery.markAsRecovered(
          vrpError, 
          'automatic_boundary_recovery'
        );
        
        globalErrorReporting.reportError(recoveredError);
        
        // Schedule retry
        this.scheduleRetry();
      }
    } catch (recoveryError) {
      console.error('[VRPErrorBoundary] Recovery failed:', recoveryError);
    }
  }

  private scheduleRetry = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }

    const delay = Math.min(1000 * (2 ** this.state.retryCount), 10000);
    
    this.retryTimeoutId = setTimeout(() => {
      this.retry();
    }, delay);
  };

  private retry = () => {
    console.log('[VRPErrorBoundary] Retrying after error recovery...');
    
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.retry);
      }

      // Default fallback based on level
      return this.renderDefaultFallback();
    }

    return this.props.children;
  }

  private renderDefaultFallback() {
    const { level, feature, component } = this.props;
    const { error, retryCount } = this.state;

    const contextInfo = [
      level,
      feature && `Feature: ${feature}`,
      component && `Component: ${component}`
    ].filter(Boolean).join(' • ');

    if (level === 'application') {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '20px',
          backgroundColor: '#f5f5f5',
          fontFamily: 'Arial, sans-serif'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '600px',
            textAlign: 'center'
          }}>
            <h1 style={{ color: '#d32f2f', marginBottom: '20px' }}>
              Application Error
            </h1>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              The VRP system encountered an unexpected error and needs to restart.
            </p>
            <details style={{ marginBottom: '20px', textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                Error Details
              </summary>
              <div style={{ 
                marginTop: '10px', 
                padding: '10px', 
                backgroundColor: '#f5f5f5',
                fontFamily: 'monospace',
                fontSize: '12px'
              }}>
                <p><strong>Context:</strong> {contextInfo}</p>
                <p><strong>Error:</strong> {error?.message}</p>
                <p><strong>Retry Count:</strong> {retryCount}</p>
              </div>
            </details>
            <div>
              <button
                onClick={this.retry}
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
              >
                Retry
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (level === 'feature') {
      return (
        <div style={{
          padding: '20px',
          margin: '20px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px'
        }}>
          <h3 style={{ color: '#856404', marginBottom: '10px' }}>
            Feature Error: {feature}
          </h3>
          <p style={{ marginBottom: '15px', color: '#856404' }}>
            This feature is temporarily unavailable due to an error.
          </p>
          <button
            onClick={this.retry}
            style={{
              backgroundColor: '#ffc107',
              color: '#856404',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry Feature
          </button>
        </div>
      );
    }

    // Component level
    return (
      <div style={{
        padding: '15px',
        margin: '10px',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '4px'
      }}>
        <h4 style={{ color: '#721c24', marginBottom: '5px' }}>
          Component Error: {component}
        </h4>
        <p style={{ marginBottom: '10px', color: '#721c24', fontSize: '14px' }}>
          Unable to render this component.
        </p>
        <button
          onClick={this.retry}
          style={{
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '5px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }
}

// HOC for easier component wrapping
export const withVRPErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options: Omit<VRPErrorBoundaryProviderProps, 'children'> = { level: 'component' }
) => {
  const WithErrorBoundary: React.FC<P> = (props) => (
    <VRPErrorBoundaryProvider {...options}>
      <WrappedComponent {...props} />
    </VRPErrorBoundaryProvider>
  );

  WithErrorBoundary.displayName = `withVRPErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;
  
  return WithErrorBoundary;
};